import AWS from 'aws-sdk';
import axios from 'axios';

class ServerAnalyticsService {
  constructor() {
    this.initializeAWS();
    this.baseUrl = process.env.REACT_APP_ANALYTICS_API_URL || 'https://api.causeandimpact.org';
    this.healthEndpoint = `${this.baseUrl}/api/health`;
    this.s3Config = {
      buckets: {
        general: process.env.REACT_APP_AWS_S3_BUCKET || 'fsapplications',
        receipts: process.env.REACT_APP_AWS_RECEIPT_BUCKET || 'sponsfi-reciept-storage'
      }
    };
    
    // MongoDB Atlas API credentials
    this.mongoDbApiPublicKey = process.env.REACT_APP_MONGODB_API_PUBLIC_KEY;
    this.mongoDbApiPrivateKey = process.env.REACT_APP_MONGODB_API_PRIVATE_KEY;
    this.mongoDbProjectId = process.env.REACT_APP_MONGODB_PROJECT_ID;
    
    // PostgreSQL connection info (for backend API)
    this.postgresHost = process.env.REACT_APP_POSTGRES_HOST;
    this.postgresDatabase = process.env.REACT_APP_POSTGRES_DATABASE;
  }

  initializeAWS() {
    try {
      AWS.config.update({
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
        region: process.env.REACT_APP_AWS_REGION
      });

      this.s3 = new AWS.S3();
      this.cloudWatch = new AWS.CloudWatch();
      
      console.log('AWS services initialized for server analytics');
    } catch (error) {
      console.error('Error initializing AWS services:', error);
      throw new Error(`AWS initialization failed: ${error.message}`);
    }
  }

  async getAllServerAnalytics() {
    try {
      console.log('Fetching all server analytics data...');
      
      // Fetch health data from your server
      const healthData = await this.getServerHealth();
      
      // Get S3 analytics
      const s3Data = await this.getS3Analytics();
      
      // Get MongoDB analytics
      const mongoData = await this.getMongoDBAnalytics();
      
      // Get PostgreSQL analytics
      const postgresData = await this.getPostgreSQLAnalytics();
      
      // Get system resources
      const systemResources = await this.getSystemResources();
      
      // Get recent events
      const recentEvents = await this.getRecentEvents();
      
      return {
        health: healthData,
        s3: s3Data,
        mongodb: mongoData,
        postgresql: postgresData,
        systemResources,
        recentEvents
      };
    } catch (error) {
      console.error('Error fetching server analytics:', error);
      throw error;
    }
  }

  async getServerHealth() {
    try {
      const response = await axios.get(`${this.healthEndpoint}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching server health:', error);
      return {
        status: 'error',
        error: error.message
      };
    }
  }

  async getS3Analytics() {
    try {
      // In a real implementation, you would call your backend API
      // For now, we'll return mock data based on your S3 bucket names
      const response = await axios.get(`${this.healthEndpoint}/api`);
      
      // Check if we have filesystem access as a proxy for S3 access
      const filesystemOk = response.data.services.find(s => s.service === 'filesystem')?.ok;
      
      return {
        buckets: [
          {
            name: this.s3Config.buckets.general,
            region: 'us-east-1',
            size: this.generateRandomSize(),
            objectCount: this.generateRandomNumber(1000, 10000),
            lastAccessed: new Date(Date.now() - this.generateRandomNumber(1, 24) * 3600000).toISOString(),
            status: filesystemOk ? 'healthy' : 'warning'
          },
          {
            name: this.s3Config.buckets.receipts,
            region: 'us-east-1',
            size: this.generateRandomSize(),
            objectCount: this.generateRandomNumber(500, 5000),
            lastAccessed: new Date(Date.now() - this.generateRandomNumber(1, 24) * 3600000).toISOString(),
            status: filesystemOk ? 'healthy' : 'warning'
          }
        ],
        totalSize: this.formatBytes(this.generateRandomNumber(5000000000, 50000000000)),
        transferRate: `${this.generateRandomNumber(1, 10).toFixed(2)} MB/s`,
        costEstimate: `$${this.generateRandomNumber(50, 200).toFixed(2)}/month`,
        status: filesystemOk ? 'operational' : 'degraded'
      };
    } catch (error) {
      console.error('Error fetching S3 analytics:', error);
      return {
        error: error.message,
        status: 'error'
      };
    }
  }

  async getMongoDBAnalytics() {
    try {
      // Get database health from your health endpoint
      const response = await axios.get(`${this.healthEndpoint}/database`);
      const isConnected = response.data.success;
      
      // In a real implementation, you would call your backend API
      // For now, we'll return mock data based on your MongoDB connection
      return {
        clusters: [
          {
            name: 'causeandimpact',
            host: 'causeandimpact.imtkn.mongodb.net',
            status: isConnected ? 'healthy' : 'error',
            collections: [
              { name: 'users', documentCount: this.generateRandomNumber(100, 1000), size: this.formatBytes(this.generateRandomNumber(1000000, 10000000)) },
              { name: 'causes', documentCount: this.generateRandomNumber(50, 500), size: this.formatBytes(this.generateRandomNumber(500000, 5000000)) },
              { name: 'applications', documentCount: this.generateRandomNumber(100, 1000), size: this.formatBytes(this.generateRandomNumber(1000000, 10000000)) },
              { name: 'donations', documentCount: this.generateRandomNumber(500, 5000), size: this.formatBytes(this.generateRandomNumber(5000000, 50000000)) }
            ],
            metrics: {
              queryPerformance: `${this.generateRandomNumber(10, 100).toFixed(2)} ms`,
              connections: this.generateRandomNumber(5, 50),
              operationsPerSecond: this.generateRandomNumber(10, 100),
              memoryUsage: `${this.generateRandomNumber(100, 1000).toFixed(2)} MB`
            }
          }
        ],
        totalDocuments: this.generateRandomNumber(10000, 100000),
        totalSize: this.formatBytes(this.generateRandomNumber(1000000000, 10000000000)),
        status: isConnected ? 'operational' : 'error'
      };
    } catch (error) {
      console.error('Error fetching MongoDB analytics:', error);
      return {
        error: error.message,
        status: 'error'
      };
    }
  }

  async getPostgreSQLAnalytics() {
    try {
      // In a real implementation, you would call your backend API
      // For now, we'll return mock data based on your PostgreSQL connection
      return {
        databases: [
          {
            name: 'sponsfidb',
            host: 'localhost',
            port: 5432,
            status: 'healthy',
            tables: [
              { name: 'users', rowCount: this.generateRandomNumber(100, 1000), size: this.formatBytes(this.generateRandomNumber(1000000, 10000000)) },
              { name: 'organizations', rowCount: this.generateRandomNumber(50, 500), size: this.formatBytes(this.generateRandomNumber(500000, 5000000)) },
              { name: 'transactions', rowCount: this.generateRandomNumber(1000, 10000), size: this.formatBytes(this.generateRandomNumber(10000000, 100000000)) },
              { name: 'fiscal_sponsors', rowCount: this.generateRandomNumber(10, 100), size: this.formatBytes(this.generateRandomNumber(100000, 1000000)) }
            ],
            metrics: {
              queryPerformance: `${this.generateRandomNumber(5, 50).toFixed(2)} ms`,
              connections: this.generateRandomNumber(5, 20),
              transactionsPerSecond: this.generateRandomNumber(5, 50),
              cacheHitRatio: `${this.generateRandomNumber(70, 99).toFixed(2)}%`
            }
          }
        ],
        totalTables: this.generateRandomNumber(20, 100),
        totalSize: this.formatBytes(this.generateRandomNumber(1000000000, 10000000000)),
        status: 'operational'
      };
    } catch (error) {
      console.error('Error fetching PostgreSQL analytics:', error);
      return {
        error: error.message,
        status: 'error'
      };
    }
  }

  async getSystemResources() {
    try {
      // In a real implementation, you would call your backend API
      // For now, we'll return mock data
      return {
        cpuUsage: this.generateRandomNumber(10, 80),
        memoryUsage: this.generateRandomNumber(20, 70),
        diskUsage: this.generateRandomNumber(30, 90)
      };
    } catch (error) {
      console.error('Error fetching system resources:', error);
      return {
        cpuUsage: 0,
        memoryUsage: 0,
        diskUsage: 0
      };
    }
  }

  async getRecentEvents() {
    try {
      // In a real implementation, you would call your backend API
      // For now, we'll return mock data
      const eventTypes = ['info', 'warning', 'error', 'success'];
      const services = ['MongoDB', 'S3', 'PostgreSQL', 'API', 'Auth'];
      const messages = [
        'Database connection established',
        'High CPU usage detected',
        'S3 bucket access denied',
        'Database query timeout',
        'API rate limit reached',
        'Authentication service restarted',
        'Backup completed successfully',
        'Low disk space warning',
        'Memory usage spike detected',
        'Database index rebuilt'
      ];
      
      const events = [];
      for (let i = 0; i < 10; i++) {
        events.push({
          id: `evt-${this.generateRandomNumber(1000, 9999)}`,
          timestamp: new Date(Date.now() - this.generateRandomNumber(1, 60) * 60000).toISOString(),
          type: eventTypes[Math.floor(Math.random() * eventTypes.length)],
          service: services[Math.floor(Math.random() * services.length)],
          message: messages[Math.floor(Math.random() * messages.length)]
        });
      }
      
      // Sort by timestamp (newest first)
      events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      return events;
    } catch (error) {
      console.error('Error fetching recent events:', error);
      return [];
    }
  }

  // Helper methods
  generateRandomNumber(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  generateRandomSize() {
    const size = this.generateRandomNumber(1000000, 10000000000);
    return this.formatBytes(size);
  }

  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
}

// Create and export a singleton instance
const serverAnalyticsService = new ServerAnalyticsService();
export default serverAnalyticsService; 